import { useMemo } from 'react';
import { useApiQuery } from '@locus-taxy/encore-ui-network';
import type { Team } from '../../types/team';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import type { ApiError } from '@locus-taxy/encore-ui-network';

interface TeamsResponse {
  data: {
    content: Array<{
      entityData: Team;
    }>;
  };
  success: boolean;
}

export function useTeams(clientId: string) {
  const searchRequest = useMemo(
    () => ({
      complexQuery: {}, // Fetch all teams
      projection: {
        'entityData.id': true,
        'entityData.name': true,
        'entityData.color': true,
      },
      pagination: {
        size: 1000,
      },
    }),
    []
  );
  const { showApiError } = useToast();

  const {
    data: teamsResponse,
    isLoading,
    error,
    refetch: fetchTeams,
  } = useApiQuery<TeamsResponse>({
    key: ['teams', clientId],
    endpointKey: 'teams.list',
    config: {
      method: 'POST',
      body: { request: searchRequest },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
    params: { clientId },
  });

  const teams = useMemo(() => {
    if (!teamsResponse?.data?.content) {
      return [];
    }
    return teamsResponse.data.content.map(
      (item: { entityData: Team }) => item.entityData
    );
  }, [teamsResponse]);

  return {
    data: teams,
    fetchTeams,
    isLoading,
    error,
    isError: !!error,
  };
}
